import { ChangeEvent, useState } from 'react';
import { Button, Divider, Grid, Link } from '@mui/material';
import CssBaseline from '@mui/material/CssBaseline';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import CircularProgress from '@mui/material/CircularProgress';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { getPasswordLogin, selectAuthState, clearCredentials, getLoginAsEntity, getMfaLogin } from '../sagas/auth/authSlice';
import { useEffect } from 'react';
import { makeStyles } from '@mui/styles';
import InputControllerLogin from '../../components/Form/InputControllerLogin';
import { useMediaQuery } from '@mui/material';
import { RightContainer } from '../../components/RightContainer';
import { Link as RouterLink } from 'react-router-dom';
import WhiteInosio from '../../icons/whiteinosio.svg';
import React from 'react';
import { EmailRegex } from '../../utils/regex';
import { RouteComponentProps, StaticContext } from 'react-router';
import { useQuery } from '../../pmui/transactions/Transactions';
import { format, subMonths } from 'date-fns';
import { Role } from '@inosio/payments-sdk';
import Copyright from '../util/Copyright';

interface State {
  email: string;
  password: string; //password flow
  mfaPasscode: string;
  showPassword: boolean;
}

type DesiredLocation = {
  destination?: string;
};

const useStyles = makeStyles((theme) => ({
  rootContainer: {
    height: '100vh',
    width: '100%',
    display: 'flex',
    flexDirection: 'row',
    background: '#FFFFFF',
    borderRadius: '24px',
  },
  rightContainer: {
    background: '#053742',
    width: '59%',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    margin: '0 auto',
  },
  leftContainer: {
    borderRadius: '24px 0px 0px 24px',
    background: '#ffffff',
    width: '42%',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    margin: '0 auto',
  },
  brandContainer: {
    display: 'flex',
    flexDirection: 'row',
    marginBottom: '30px',
  },
  brandTextContainer: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    marginLeft: '36px',
  },
  brandText: {
    fontFamily: 'BMWHelvetica',
    fontWeight: 700,
    fontSize: '24px',
  },
  formContainer: {
    backgroundColor: '#FFFFFF',
    borderRadius: '20px',
    width: '340px',
    border: 'none',
  },
  buttonContainer: {
    width: '340px',
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'space-between',
    justifyContent: 'space-between',
    marginTop: '20px',
    paddingBottom: '20px',
  },
  wrapper: {
    margin: '1px',
    position: 'relative',
  },
  buttonProgress: {
    color: '#053742',
    position: 'absolute',
    top: '50%',
    left: '50%',
    marginTop: -12,
    marginLeft: -12,
  },
  globalError: {
    display: 'flex',
    flexDirection: 'row',
    marginBottom: '30px',
    borderTop: '2px  solid #DB3939',
    borderRadius: '24px',
    padding: '12px 20px',
    width: '340px',
  },
  btnStyle: {
    font: '14px Work Sans 400 !important',
    width: '113px',
    height: '49px',
    borderRadius: '24px !important',
    textTransform: 'capitalize',
    backgroundColor: '#39A2DB',
    color: '#FFFFFF !important',
  },
  btnStyleMobileScreen: {
    font: '14px Work Sans !important',
    width: '100%',
    height: '49px',
    borderRadius: '24px !important',
    textTransform: 'capitalize',
  },
  newAccountText: {
    color: 'rgba(211, 211, 211, 1)',
    marginLeft: '25px',
    fontSize: '16px',
    marginBottom: '5px',
  },
  isActive: {
    background: 'rgba(57, 162, 219, 1) !important',
    boxShadow: ' 0px 0px 40px rgba(0, 0, 0, 0.1) !important',
    color: '#fff !important',
  },
  isNotActive: {
    background: 'rgba(211, 211, 211, 1) !important',
    boxShadow: ' 0px 0px 40px rgba(0, 0, 0, 0.1) !important',
  },
  brandContainerMobileScreen: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'start',
    justifyContent: 'start',
    marginBottom: '30px',
  },
  brandTextMobileScreen: {
    fontSize: '20px !important',
  },
  buttonContainerMobileScreen: {
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    // marginTop: "170px",
    paddingBottom: '20px',
  },
  signUpBtn: {
    font: '14px Work Sans !important',
    border: '1px solid #39A2DB !important',
    width: '113px',
    height: '49px',
    borderRadius: '24px !important',
  },
  forgetText: {
    font: '14px Work Sans',
    lineHeight: '30px',
    color: '#39A2DB',
    textDecoration: 'none !important',
    maxHeight: '30px !important',
    margin: '10px !important',
    marginTop: '8px !important',
    marginBottom: '27px !important',
    paddingLeft: '5px !important',
    cursor: 'pointer',
    zIndex: 30,
  },
}));

const theme = createTheme({
  typography: {
    fontFamily: "'Work Sans', sans-serif",
    fontSize: 16,
  },
});

enum authMode {
  Password,
  Mfa,
}

export default function SignIn(props: RouteComponentProps<{}, StaticContext, DesiredLocation>) {
  const { location } = props;
  const matchesMobile = useMediaQuery('(max-width:600px)');
  const matches900 = useMediaQuery('(max-width:900px)');
  const matches1200 = useMediaQuery('(max-width:1200px)');
  const classes = useStyles();
  const history = useHistory();
  const dispatch = useDispatch();
  const { isFetching, mfaRequired, isSuccess, isError, errorMessage, isLockedForSecurity, wrongPasswordAttempt, wrongPasswordAttemptErrorMessage, ipaUser, isLogAsSuccess } =
    useSelector(selectAuthState);

  const [values, setValues] = React.useState<State>({
    email: '',
    password: '',
    mfaPasscode: '',
    showPassword: false,
  });

  const handleChange = (prop: keyof State) => (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setValues({ ...values, [prop]: event.target.value });
  };

  const [emailError, setEmailError] = useState('');
  const [passError, setPassError] = useState('');
  const [globalError, setGlobalError] = useState('');
  const [authModeStep, setAuthModeStep] = useState(authMode.Password);
  //on initial load of the ledger component we wanna show this date range of ledger activity
  const defaultLedgerActivityStartDate = format(subMonths(new Date(), 2), 'yyyy-MM-dd');
  const defaultLedgerActivityEndDate = format(new Date(), 'yyyy-MM-dd');
  const class1 = !matchesMobile ? classes.btnStyle : classes.btnStyleMobileScreen;
  const query = useQuery();
  const logAsProfileUuid = query.get('logAsEntityUuid');
  const logAsUserUuid = query.get('logAsUserUuid');
  //i don't like how this possibly collides with props.location.state.destination
  const logAsDestination = query.get('logAsDestination'); //custom destination. ie, specific application.
  const class2 = classes.isActive;

  React.useEffect(() => {
    if (logAsProfileUuid) {
      dispatch(getLoginAsEntity({ ProfileUuid: logAsProfileUuid, UserUuid: logAsUserUuid || undefined }));
    }
  }, [dispatch, logAsProfileUuid]);

  const validateEmail = (inputedEmail: string) => {
    if (!inputedEmail) {
      setEmailError(() => 'Email address is empty!');
      return false;
    } else if (!EmailRegex.test(inputedEmail.trim())) {
      setEmailError(() => 'Invalid email address!');
      return false;
    }
    setEmailError('');
    return true;
  };
  const validatePassword = (inputtedPassword: string) => {
    if (inputtedPassword.length === 0) {
      setPassError('Password is empty!');
      return false;
    } else {
      setPassError('');
      return true;
    }
  };

  const handlePasswordLoginSubmit = (event: any) => {
    event?.preventDefault();

    if (validateEmail(values.email) && validatePassword(values.password)) {
      dispatch({
        type: getPasswordLogin.type,
        payload: {
          email: values.email.trim(),
          password: values.password,
        },
      });
    }
  };

  const handleMfaLoginSubmit = (event: any) => {
    event?.preventDefault();

    dispatch({
      type: getMfaLogin.type,
      payload: {
        passcode: values.mfaPasscode,
      },
    });
  };

  const sendUserToLandingPage = () => {
    if (ipaUser?.mustChangePassword) {
      //if mustChangePassword go to change password page
      history.push({
        pathname: '/changepassword',
        state: {
          mustChangePassword: true,
          username: ipaUser?.username,
          oldPassword: values.password,
        },
      });

      dispatch(clearCredentials()); //clear login session
      return;
    }

    //these take higher precedence than the person type.
    if (logAsDestination) {
      history.push(logAsDestination);
      return;
    }

    //if they originally intended to be somewhere, then send them. ie, deep links prior to login
    if (location?.state?.destination) {
      history.push(location?.state?.destination);
      return;
    }

    //else find out who they are and send them accordingly
    const principal = ipaUser?.currentProfile;
    const currentRole = principal?.role;

    switch (currentRole) {
      case Role.InosioAdmin:
      case Role.InosioStaff:
        history.push('/adminportal');
        break;
      case Role.Resident:
        history.push('/resui/payments');
        break;
      case Role.PropertyManager:
      case Role.PropertyManagerAdmin:
      case Role.MaintenanceCoordinator:
      case Role.MaintenanceStaff:
      case Role.Vendor:
        history.push('/pmui');
        break;
      case Role.Applicant:
        history.push('/resui/applications');
        break;
      case Role.Owner:
        //send them to the ledger page with this date range
        history.push(`/ownerui/ledger?startDate=${defaultLedgerActivityStartDate}&endDate=${defaultLedgerActivityEndDate}`);
        break;
      default:
        break;
    }
  };

  useEffect(() => {
    if (isError) {
      setGlobalError('The email or password is invalid. Please try again');
    }

    if (mfaRequired) {
      setAuthModeStep(authMode.Mfa);
      return;
    } else {
      setAuthModeStep(authMode.Password);
    }

    if (isLogAsSuccess || (!logAsProfileUuid && isSuccess)) {
      sendUserToLandingPage();
    }

    if (errorMessage) setGlobalError(errorMessage);
  }, [isError, mfaRequired, errorMessage, isSuccess, isLogAsSuccess, ipaUser, dispatch, history]);

  return (
    <ThemeProvider theme={theme}>
      <div className={classes.rootContainer}>
        <CssBaseline />
        <div className={classes.leftContainer}>
          {globalError.length ? (
            <div className={classes.globalError}>
              <Typography
                style={{
                  fontFamily: 'BMWHelvetica',
                  fontStyle: 'normal',
                  fontWeight: 500,
                  fontSize: '14px',
                  lineHeight: '18px',
                  color: '#DB3939',
                }}
                data-e2e="globalError"
              >
                {' '}
                {globalError}
                {wrongPasswordAttempt > 1 && !isLockedForSecurity ? ' Your account will be locked after 4 failed attempts.' : ''}
              </Typography>
            </div>
          ) : (
            ''
          )}
          <div className={`${!matchesMobile ? classes.brandContainer : classes.brandContainerMobileScreen}`}>
            <Box pb={6} sx={{ display: 'flex', alignItems: 'center' }}>
              <img src={WhiteInosio} style={{ width: '150px', height: '150px' }} alt="logo" />
            </Box>
          </div>

          {authModeStep === authMode.Password ? (
            <Box component="form" name="passwordFlow" onSubmit={handlePasswordLoginSubmit} noValidate className={classes.formContainer}>
              <div>
                <InputControllerLogin
                  name={'email'}
                  label={'Email'}
                  onChange={handleChange('email')}
                  value={values.email}
                  autoComplete="email"
                  isError={!!emailError.length}
                  errors={emailError}
                  isfullWidth={true}
                />
              </div>
              <div style={{ display: 'flex' }}>
                <InputControllerLogin
                  name={'password '}
                  label={'Password'}
                  onChange={handleChange('password')}
                  required={true}
                  autoComplete="password"
                  isError={!!passError.length || !wrongPasswordAttemptErrorMessage}
                  errors={passError || wrongPasswordAttemptErrorMessage}
                  value={values.password}
                  isfullWidth={true}
                  type="password"
                />
              </div>
              <div className={`${!matchesMobile ? classes.buttonContainer : classes.buttonContainerMobileScreen}`}>
                {!matchesMobile ? (
                  <div className={classes.wrapper}>
                    <Button href="/signup" className={classes.signUpBtn}>
                      Sign Up
                    </Button>
                  </div>
                ) : (
                  <span className={classes.newAccountText}>
                    {' '}
                    Don't have an account yet?&nbsp;
                    <Link style={{ color: '#39A2DB', textDecoration: 'none', cursor: 'pointer' }} component={RouterLink} to="/signup">
                      SignUp
                    </Link>{' '}
                  </span>
                )}
                <div className={classes.wrapper}>
                  <Button disabled={isFetching} type="submit" className={`${class1} ${class2}`} id="signInButton">
                    Sign in
                  </Button>
                  {isFetching ? (
                    <CircularProgress
                      size={26}
                      sx={{
                        color: '#39A2DB',
                      }}
                      className={classes.buttonProgress}
                    />
                  ) : (
                    <></>
                  )}
                </div>
              </div>
              <Divider style={{ width: '100%' }} />
              <Grid item sx={{ display: 'flex', justifyContent: 'space-evenly' }}>
                <Link href="http://support.inosio.com/" className={classes.forgetText}>
                  Need Help?
                </Link>
                <Grid item sx={{ height: '30px', marginTop: '8px' }}>
                  <Divider orientation="vertical" flexItem style={{ height: '100%' }} />
                </Grid>
                <Link component={RouterLink} to="/forgetpassword" className={classes.forgetText}>
                  Forgot Password?
                </Link>
              </Grid>
            </Box>
          ) : (
            <Box component="form" name="mfaFlow" onSubmit={handleMfaLoginSubmit} noValidate className={classes.formContainer}>
              <div>
                <Typography sx={{ fontWeight: 600, fontSize: '20px', fontFamily: 'Work Sans', mb: 5 }}> Mutli-factor authentication</Typography>
                <Typography sx={{ fontWeight: 400, fontSize: '16px', fontFamily: 'Work Sans', mb: 5 }}>
                  Your account is secured with multi-factor authentication. To continue, please enter the Inosio passcode found in your authenticator app.
                </Typography>
              </div>

              <div style={{ marginTop: 20, marginBottom: 20 }}>
                <Typography sx={{ fontSize: '16px', fontFamily: 'Work Sans' }}>
                  <Box component="span" sx={{ fontWeight: 600, mr: 1 }}>
                    Email:
                  </Box>
                  {values.email}
                </Typography>
              </div>
              <div style={{ display: 'flex' }}>
                <InputControllerLogin name={'passcode '} label={'MFA Passcode'} onChange={handleChange('mfaPasscode')} required={true} value={values.mfaPasscode} isfullWidth={true} />
              </div>
              <div>
                <div>
                  <Button
                    disabled={isFetching}
                    type="submit"
                    sx={{
                      font: '14px Work Sans 400 !important',
                      width: '100%',
                      height: '49px',
                      borderRadius: '24px !important',
                      textTransform: 'capitalize',
                      backgroundColor: '#39A2DB',
                      color: '#FFFFFF !important',
                      marginTop: 2,
                      '&:hover': {
                        backgroundColor: '#2680C2',
                      },
                    }}
                  >
                    Continue
                  </Button>
                  {isFetching ? (
                    <CircularProgress
                      size={26}
                      sx={{
                        color: '#39A2DB',
                      }}
                      className={classes.buttonProgress}
                    />
                  ) : (
                    <></>
                  )}
                </div>
              </div>
            </Box>
          )}
          <Copyright sx={{ pt: 1, fontSize: 11 }} />
        </div>
        {!(matches900 && matches1200) ? <RightContainer /> : <></>}
      </div>
    </ThemeProvider>
  );
}
